/**
 * Wanneer een speelfiguur van eigenaar wisselt, en wat je dan te horen krijgt.
 *
 * De ridderfiguur en de molen zijn allebei een overwinningspunt waard (handboek p. 11).
 * Ze wisselen zonder aankondiging: de kern rekent na elke zet opnieuw uit wie de sterkste
 * riddermacht en de grootste handelsmacht heeft, en zet het beeldje bij een andere naam.
 * Dat is te weinig voor iets wat een punt waard is, dus komt er een banner bij.
 *
 * Eén melding per figuur, en niet meer dan dat:
 *
 * - gaat de figuur naar een speler, dan hoor je alléén dat hij hem heeft — ook als hij
 *   hem van de ander afpakte. Twee vensters voor één gebeurtenis is er een te veel.
 * - wordt het gelijkspel, dan is er geen winnaar en hoor je juist alleen dat de vorige
 *   houder hem kwijt is.
 * - verandert er niets, dan zeg je niets.
 *
 * Deze rekenkant staat los van het scherm zodat hij te toetsen is — net als `betalen.ts`,
 * dat er om dezelfde reden apart staat.
 */
import type { Seat } from '@catan/rules';

/** De twee speelfiguren, met de naam waaronder hun banner en symbool bekend zijn. */
export type Figuur = 'riddermacht' | 'handelsmacht';

/** Wie de figuren heeft; `undefined` betekent gelijkspel — niemand dus. */
export interface Figuren {
  readonly knight: Seat | undefined;
  readonly windmill: Seat | undefined;
}

export interface FiguurMelding {
  readonly figuur: Figuur;
  readonly tekst: string;
}

/** Hoe de figuur in een zin heet. */
const OPSCHRIFT: Readonly<Record<Figuur, string>> = {
  riddermacht: 'de Riddermacht',
  handelsmacht: 'de Handelsmacht',
};

/**
 * Wat er te melden valt nu de figuren opnieuw berekend zijn.
 *
 * @param naam hoe deze speler heet; het spel noemt jou voorlopig "Jij".
 * @param jij  welke stoel de mens is. Tegen jou spreekt het spel in de tweede persoon —
 *             "Jij hebt", niet "Jij heeft" — en over Chi in de derde.
 */
export function figuurMeldingen(
  voor: Figuren,
  na: Figuren,
  naam: (seat: Seat) => string,
  jij: Seat,
): FiguurMelding[] {
  const meldingen: FiguurMelding[] = [];
  const heeft = (seat: Seat): string => (seat === jij ? 'hebt' : 'heeft');
  const is = (seat: Seat): string => (seat === jij ? 'bent' : 'is');

  const beoordeel = (figuur: Figuur, eerder: Seat | undefined, nu: Seat | undefined): void => {
    if (eerder === nu) return;
    if (nu !== undefined) {
      meldingen.push({ figuur, tekst: `${naam(nu)} ${heeft(nu)} ${OPSCHRIFT[figuur]}` });
      return;
    }
    // Niemand heeft hem meer: gelijke sterkte. Dan is er alleen een verliezer.
    if (eerder !== undefined) {
      meldingen.push({
        figuur,
        tekst: `${naam(eerder)} ${is(eerder)} ${OPSCHRIFT[figuur]} kwijt`,
      });
    }
  };

  beoordeel('riddermacht', voor.knight, na.knight);
  beoordeel('handelsmacht', voor.windmill, na.windmill);
  return meldingen;
}
